// ============================================================
// BLOG GENSOL - Archivo de contenido editable
// ============================================================
// Cada artículo permite editar de forma independiente:
//   - slug:            URL amigable (/blog/tu-slug)
//   - title:           H1 único del artículo
//   - metaTitle:       Etiqueta <title> para Google
//   - metaDescription: Meta descripción para Google
//   - category:        Debe coincidir con un id de blogCategories
//   - image / imageAlt: Imagen destacada y su texto ALT
//   - date:            Fecha de publicación (YYYY-MM-DD)
//   - excerpt:         Extracto mostrado en la cuadrícula del blog
//   - ctaText / ctaMessage: (opcional) Texto del botón CTA de WhatsApp
//                      al final del artículo y mensaje pre-llenado
//   - content:         Bloques del cuerpo. Tipos disponibles:
//       { type: 'h2', text: '...' }
//       { type: 'h3', text: '...' }
//       { type: 'h4', text: '...' }
//       { type: 'p',  text: '...' }
//       { type: 'ul', items: ['...', '...'] }
//       { type: 'tip', text: '...' }  (caja destacada naranja)
// El tiempo de lectura se calcula automáticamente.
// ============================================================

export const blogCategories = [
  { id: 'generadores', label: 'Generadores' },
  { id: 'soldadoras', label: 'Soldadoras' },
  { id: 'torres-de-iluminacion', label: 'Torres de Iluminación' },
  { id: 'mantenimiento', label: 'Mantenimiento' },
];

export const blogAuthor = {
  name: 'Especialista GENSOL',
  role: 'Equipo técnico con más de 25 años de experiencia en energía industrial',
  description:
    'Especialistas en renta, reparación y mantenimiento de generadores, soldadoras y torres de iluminación en Veracruz y el sureste de México.',
};

export const blogPosts = [
  {
    slug: 'instalar-planta-de-luz-automatica-en-veracruz',
    title: '¿Estás pensando en instalar una planta de luz automática en Veracruz? Esto es lo que debes tomar en cuenta a largo plazo',
    metaTitle: 'Instalar una Planta de Luz Automática en Veracruz: Guía de Costos | GENSOL',
    metaDescription:
      'Conoce los costos reales de instalar una planta de luz automática con ATS en Veracruz: equipo, instalación, obra civil, consumo y mantenimiento a largo plazo.',
    category: 'generadores',
    image:
      'https://customer-assets.emergentagent.com/job_44da33e2-9df0-4e2d-82b0-9d3a733fa63e/artifacts/7d1x5kja_Planta%20de%20luz%20de%2020Kw.png',
    imageAlt: 'Planta de luz automática residencial de 20 kW con caseta acústica en Veracruz',
    date: '2026-06-15',
    excerpt:
      'Equipo, instalación con obra civil y mantenimiento: las tres categorías de gastos que debes evaluar antes de instalar un generador automático con ATS en tu hogar.',
    ctaText: 'Cotiza tu generador automatizado hoy mismo',
    ctaMessage:
      'Hola GENSOL, me interesa cotizar un generador automatizado con tablero de transferencia (ATS) para mi hogar. ¿Me pueden asesorar?',
    content: [
      { type: 'p', text: 'Debido a las altas temperaturas, los constantes apagones y las condiciones climáticas de la región, considerar la instalación de un generador eléctrico residencial (planta de luz) con un Tablero de Transferencia Automática (ATS) es una excelente decisión para asegurar energía continua y sin interrupciones en tu hogar.' },
      { type: 'p', text: 'Sin embargo, hay ciertos factores que debes evaluar antes de decidir si la automatización es la mejor opción para ti. Para presupuestar este proyecto de manera realista, dividiremos los gastos en tres categorías principales:' },
      { type: 'ul', items: ['El equipo.', 'La instalación (incluyendo la obra civil).', 'Los costos operativos y de mantenimiento.'] },
      { type: 'h2', text: '1. El equipo' },
      { type: 'p', text: 'Los generadores eléctricos estacionarios automáticos para uso residencial suelen cubrir un rango de 10 kW hasta 50 kW, dependiendo de la potencia necesaria para alimentar el hogar.' },
      { type: 'tip', text: 'TIP de Gensol: Es posible dividir la carga eléctrica para energizar únicamente ciertas áreas críticas (como la cocina, la recámara principal, etc.) según cómo esté diseñada tu instalación eléctrica. De esta forma, disminuyes considerablemente el costo del equipo, su mantenimiento y su consumo de combustible.' },
      { type: 'p', text: 'En Gensol contamos con un amplio catálogo de alta gama. Para uso doméstico, recomendamos la marca GENMANN, ya que cuenta con caseta acústica silenciosa y tablero de transferencia con una excelente relación calidad-precio.' },
      { type: 'h3', text: '¿Qué es el ATS?' },
      { type: 'p', text: 'El Tablero de Transferencia Automática (ATS, por sus siglas en inglés) es el "cerebro" del sistema: detecta el corte de luz de la calle, enciende el generador de forma autónoma y realiza el cambio de energía en cuestión de segundos.' },
      { type: 'h2', text: '2. La instalación: adecuaciones, mano de obra y materiales' },
      { type: 'p', text: 'La instalación de una planta de luz y su tablero de transferencia requiere de profesionales certificados debido al manejo de alta tensión y líneas de gas. En Gensol contamos con personal calificado para llevar a cabo todo el proceso logístico y de instalación de manera segura.' },
      { type: 'p', text: 'Al elaborar tu presupuesto, debes tomar en cuenta los siguientes conceptos de instalación:' },
      { type: 'h3', text: 'Mano de obra eléctrica' },
      { type: 'p', text: 'Un técnico especialista debe cablear y conectar el ATS entre tu medidor de luz exterior y el panel de distribución principal. Este trabajo suele tomar de 2 a 3 días de labor profesional.' },
      { type: 'h3', text: 'Obra civil (base de concreto)' },
      { type: 'p', text: 'El equipo debe asentarse sobre una superficie nivelada, firme y completamente libre de humedad. Especialmente en Veracruz, debido al riesgo de inundaciones y lluvias fuertes, es indispensable colar una plataforma de cemento elevada para proteger tu inversión.' },
      { type: 'h2', text: '3. Costos operativos y de mantenimiento a largo plazo' },
      { type: 'h3', text: 'Consumo de combustible' },
      { type: 'p', text: 'El costo de operación depende directamente de la capacidad en kilowatts (kW) del equipo y de la carga conectada. En tu cotización formal, siempre encontrarás la ficha técnica del generador con su consumo estimado por hora.' },
      { type: 'h3', text: 'Mantenimiento preventivo anual' },
      { type: 'p', text: 'Al igual que un automóvil, tu planta de luz requiere servicios preventivos periódicos que incluyan el cambio de aceite, filtros, bujías y pruebas de batería. La frecuencia dependerá de las horas de uso acumuladas y del clima de la zona. En Gensol ofrecemos pólizas de mantenimiento anual y visitas operativas para el chequeo de niveles, garantizando que tu equipo responda cuando más lo necesites.' },
      { type: 'h2', text: 'Conclusión' },
      { type: 'p', text: 'La instalación de un generador automatizado es una inversión sumamente valiosa en zonas con apagones frecuentes, o cuando no se cuenta con una persona en sitio que pueda realizar el cambio de corriente de forma manual y segura. Analizar estos costos de instalación y mantenimiento desde el inicio te permitirá tomar la mejor decisión para proteger la comodidad y seguridad de tu familia.' },
    ],
  },
];

// Tiempo de lectura estimado (200 palabras por minuto)
export const getReadingTime = (post) => {
  const words = post.content
    .map((b) => (b.type === 'ul' ? b.items.join(' ') : b.text))
    .join(' ')
    .split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

export const getPostBySlug = (slug) => blogPosts.find((p) => p.slug === slug);

export const getRelatedPosts = (post, max = 3) =>
  blogPosts.filter((p) => p.category === post.category && p.slug !== post.slug).slice(0, max);

export const getCategoryLabel = (id) =>
  blogCategories.find((c) => c.id === id)?.label || id;

export const formatPostDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
